/**
 * The portal's top bar — the two things that belong ABOVE every screen rather
 * than inside one: the command palette trigger and the approvals bell.
 *
 * UI_CONSOLIDATION_PLAN 4B + 4C. Until now the portal had no top bar at all
 * (`Base.js` was sidebar + content), which is why Approvals had to live as a
 * nav item and why there was nowhere to say "Ctrl-K opens everything". A
 * shortcut nobody is told about is a shortcut nobody uses, so the trigger is
 * visible and shows its own key.
 *
 * Deliberately thin and transparent: it must not steal vertical space from the
 * page headers every hub already draws, and it must not look like a second
 * navigation. It renders nothing at all when neither child has anything to show
 * (the bell hides itself without the `approvals` capability, and on a narrow
 * screen the palette trigger collapses to its icon).
 */
import React from 'react';
import { Search } from 'lucide-react';

import ApprovalsBell from './ApprovalsBell';
import CommandPalette from './CommandPalette';

const isMac = () => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');
};

// The palette owns its own open state and listens for this event, so the
// trigger needs no shared context and there is only ever one palette mounted.
const openPalette = () =>
  window.dispatchEvent(new CustomEvent('inikru:open-command-palette'));

const TopBar = () => (
  <>
    <div className="flex items-center justify-end gap-2 px-4 pt-3">
      <button
        type="button"
        onClick={openPalette}
        className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-2 text-[12.5px] text-muted transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        aria-label="Search and jump to any screen"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Search or jump to…</span>
        <kbd className="hidden sm:inline rounded border border-line px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
          {isMac() ? '⌘K' : 'Ctrl K'}
        </kbd>
      </button>
      <ApprovalsBell />
    </div>
    <CommandPalette />
  </>
);

export default TopBar;
